// The tab icon: Frontier's F, turning in 3D.
//
// Chrome draws only the first frame of an animated SVG or GIF icon, so the one
// way to move a tab icon there is to hand the browser a new image per frame.
// Every frame of one turn is drawn once, on a 32px canvas, and kept as a PNG
// data URL; after that a frame costs one `href` assignment. The static icons in
// the layout stay the fallback: they are what shows without JavaScript, without
// a 2D canvas, to someone who asked for reduced motion, and in Safari, which
// ignores icon changes made after load. A tab in the background rests on the
// first frame, the F face on.

const SIZE = 32
const FRAMES = 36
const TURN_MS = 2400

// public/icon.svg, in its own 512 units: the F as one outline, then the slant.
// Drawn larger than the file has it, which leaves room for a home screen mask
// that a 16px tab does not need.
const UNITS = 512
const CENTER = UNITS / 2
const SLANT = Math.tan((12 * Math.PI) / 180)
const ENLARGE = 1.4
const OUTLINE: Point[] = (
  [
    [196, 120],
    [372, 120],
    [372, 164],
    [240, 164],
    [240, 230],
    [328, 230],
    [328, 270],
    [240, 270],
    [240, 392],
    [196, 392],
  ] as const
).map(([x, y]) => [(x - SLANT * y + 40 - CENTER) * ENLARGE, (y - CENTER) * ENLARGE])

const HALF_DEPTH = 48
const CAMERA = 1100
const BACKGROUND = '#020202'
const INK = [0x9c, 0xe5, 0xf5] as const

type Point = readonly [number, number]

export type Face = { points: Point[]; color: string }

function shade(light: number): string {
  return `rgb(${INK.map((channel) => Math.round(channel * light)).join(', ')})`
}

// The faces of the extruded F turned `angle` radians about its vertical axis,
// in 512-unit icon coordinates, ordered back to front for painting.
export function faviconFaces(angle: number): Face[] {
  const sin = Math.sin(angle)
  const cos = Math.cos(angle)

  const depthOf = (x: number, z: number) => -x * sin + z * cos
  const project = ([x, y]: Point, z: number): Point => {
    const scale = CAMERA / (CAMERA - depthOf(x, z))
    return [CENTER + (x * cos + z * sin) * scale, CENTER + y * scale]
  }

  const walls = OUTLINE.flatMap((from, index) => {
    const to = OUTLINE[(index + 1) % OUTLINE.length] as Point
    const [dx, dy] = [to[0] - from[0], to[1] - from[1]]
    // Outward normal of this edge, then how squarely it faces the viewer.
    const facing = (-dy / Math.hypot(dx, dy)) * sin
    if (facing <= 0.01) return []

    return [
      {
        depth: depthOf((from[0] + to[0]) / 2, 0),
        face: {
          points: [
            project(from, HALF_DEPTH),
            project(to, HALF_DEPTH),
            project(to, -HALF_DEPTH),
            project(from, -HALF_DEPTH),
          ],
          color: shade(0.3 + 0.35 * facing),
        },
      },
    ]
  })

  const faces = walls.sort((a, b) => a.depth - b.depth).map((wall) => wall.face)

  // Whichever cap faces the viewer goes on last. Edge on, there is none.
  if (Math.abs(cos) > 0.01) {
    const z = cos > 0 ? HALF_DEPTH : -HALF_DEPTH
    faces.push({ points: OUTLINE.map((point) => project(point, z)), color: shade(1) })
  }

  return faces
}

// One turn as PNG data URLs, or null where there is no 2D canvas to draw on.
export function renderFaviconFrames(): string[] | null {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = SIZE
  const context = canvas.getContext('2d')
  if (!context) return null

  context.scale(SIZE / UNITS, SIZE / UNITS)

  return Array.from({ length: FRAMES }, (_, frame) => {
    context.fillStyle = BACKGROUND
    context.fillRect(0, 0, UNITS, UNITS)

    for (const { points, color } of faviconFaces((frame / FRAMES) * 2 * Math.PI)) {
      context.fillStyle = color
      context.beginPath()
      points.forEach(([x, y], index) => (index ? context.lineTo(x, y) : context.moveTo(x, y)))
      context.closePath()
      context.fill()
    }

    return canvas.toDataURL('image/png')
  })
}

let stop: (() => void) | null = null

export function startSpinningFavicon(render: () => string[] | null = renderFaviconFrames): void {
  if (stop || typeof document === 'undefined' || typeof window.matchMedia !== 'function') return

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const spinning = document.createElement('link')
  spinning.rel = 'icon'
  spinning.type = 'image/png'

  let frames: string[] | null | undefined
  let fallbacks: HTMLLinkElement[] = []
  let timer: ReturnType<typeof setInterval> | null = null

  const pause = () => {
    if (timer === null) return
    clearInterval(timer)
    timer = null
    // Rest face on, not on a frame caught mid-turn.
    if (frames?.[0]) spinning.href = frames[0]
  }

  const play = () => {
    if (timer !== null || document.hidden || reducedMotion.matches) return
    if (frames === undefined) frames = render()
    if (!frames?.length) return

    const turn = frames
    let frame = 0
    spinning.href = turn[0] as string

    // A browser picks among several icon links by its own rules, so once the F
    // turns it is the only one in the head.
    if (!spinning.isConnected) {
      fallbacks = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'))
      if (fallbacks[0]) fallbacks[0].before(spinning)
      else document.head.append(spinning)
      for (const link of fallbacks) link.remove()
    }

    timer = setInterval(() => {
      frame = (frame + 1) % turn.length
      spinning.href = turn[frame] as string
    }, TURN_MS / turn.length)
  }

  const sync = () => (document.hidden || reducedMotion.matches ? pause() : play())

  document.addEventListener('visibilitychange', sync)
  reducedMotion.addEventListener('change', sync)
  stop = () => {
    pause()
    for (const link of fallbacks) spinning.before(link)
    spinning.remove()
    document.removeEventListener('visibilitychange', sync)
    reducedMotion.removeEventListener('change', sync)
  }

  play()
}

// Test seam: the running animation is module state.
export function stopSpinningFaviconForTests(): void {
  stop?.()
  stop = null
}
